import React, { useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import Header from "./Header";
import Footer from "./Footer";
import { Upload, FileText, AlertCircle } from "lucide-react";

const UploadPage = ({ setExtractedData }) => {
  const [file, setFile] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const navigate = useNavigate();
  const fileInputRef = useRef(null);

  // File validation functions
  const ALLOWED_TYPES = ['application/pdf', 'application/vnd.openxmlformats-officedocument.wordprocessingml.document'];
  const MAX_FILE_SIZE = 10 * 1024 * 1024; // 10MB

  const validateFile = (file) => {
    if (!ALLOWED_TYPES.includes(file.type)) {
      setError('Please upload only PDF or DOCX files');
      return false;
    }
    if (file.size > MAX_FILE_SIZE) {
      setError('File size must be less than 10MB');
      return false;
    }
    return true;
  };

  // Function to extract text from PDF using vanilla JS
  const extractTextFromPDF = async (file) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = (e) => {
        const typedArray = new Uint8Array(e.target.result);
        
        // Fallback error handler
        const handleError = (err) => {
          console.error("PDF parsing error:", err);
          reject(new Error("Unable to extract PDF text"));
        };

        // Try parsing PDF text
        try {
          let extractedText = '';
          
          // Simulate basic text extraction
          const decoder = new TextDecoder('utf-8');
          const text = decoder.decode(typedArray);
          
          // Extract text using basic regex and filtering
          const textLines = text.split(/\n/).filter(line => 
            line.trim().length > 0 && 
            !/^[\W\d]+$/.test(line) && 
            line.split(' ').length > 1
          );

          extractedText = textLines.join(' ').substring(0, 5000);
          
          resolve(extractedText || "No readable text found in PDF");
        } catch (err) {
          handleError(err);
        }
      };
      
      reader.onerror = () => reject(new Error("File reading error"));
      reader.readAsArrayBuffer(file);
    });
  };

  // Function to extract text from DOCX using mammoth
  const extractTextFromDOCX = async (file) => {
    const mammoth = await import('mammoth');
    const arrayBuffer = await file.arrayBuffer();
    const result = await mammoth.extractRawText({ arrayBuffer });
    return result.value;
  };

  // Function to process the medical report using OpenAI API
  const processReport = async (reportText) => {
    const OPENAI_API_KEY = process.env.REACT_APP_OPENAI_API_KEY;
  
    const messages = [
      { role: "system", content: "You are a highly skilled medical assistant AI." },
      {
        role: "user",
        content: `Extract the following information from the medical report in a structured format:
          1. Name
          2. Date of Birth (DOB)
          3. Gender
          4. Medical Record Number (with MRN)
          5. Current Diagnoses (with dates)
          6. Medications (name, dosage, frequency)
          7. Allergies (type and reaction)
          8. Recent Lab Results (last 6 months)
          9. Past Surgeries/Procedures (name, with date,and outcome)
          10. Chief Complaint (reason for visit)
          11. Doctor's Notes (summary and treatment plan)
          12. Detail summary (a complete summary including suggestions with 150 words. No need to include MRN. Include health tips)
  
          Medical Report:
          ${reportText}`
      }
    ];
  
    const response = await fetch('https://api.openai.com/v1/chat/completions', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${OPENAI_API_KEY}`
      },
      body: JSON.stringify({
        model: "gpt-3.5-turbo",
        messages,
        max_tokens: 1500,
        temperature: 0.5
      })
    });
  
    if (!response.ok) {
      throw new Error('Failed to process the report');
    }
  
    const data = await response.json();
    return data.choices[0].message.content.trim();
  };

  const handleFileUpload = async () => {
    if (!file) {
      setError("Please select a file to upload.");
      return;
    }

    if (!validateFile(file)) {
      return;
    }

    setLoading(true);
    setError("");

    try {
      // Extract text based on file type
      let reportText;
      if (file.type === 'application/pdf') {
        reportText = await extractTextFromPDF(file);
      } else {
        reportText = await extractTextFromDOCX(file);
      }

      // Process the report using OpenAI
      const extractedData = await processReport(reportText);
      setExtractedData(extractedData);
      navigate("/results");
    } catch (err) {
      setError(err.message);
      console.error('Error processing file:', err);
    } finally {
      setLoading(false);
    }
  };

  // Drag and drop handlers
  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    
    const droppedFiles = e.dataTransfer.files;
    if (droppedFiles.length > 0) {
      const selectedFile = droppedFiles[0];
      if (validateFile(selectedFile)) {
        setFile(selectedFile);
      }
    }
  };

  const handleFileInputChange = (e) => {
    const selectedFile = e.target.files[0];
    if (selectedFile) {
      setFile(selectedFile);
    }
  };

  return (
    <div 
      className="min-h-screen flex flex-col bg-slate-900"
      onDragOver={handleDragOver}
      onDrop={handleDrop}
    >
      <Header />
      <main className="flex-grow p-8 flex items-center justify-center">
        <div className="max-w-md w-full space-y-8">
          <div className="text-center">
            <h2 className="text-3xl font-bold bg-gradient-to-r from-cyan-400 to-blue-500 bg-clip-text text-transparent">
              Upload Medical Report
            </h2>
            <p className="mt-2 text-slate-400">
              Upload your medical report in PDF or DOCX format
            </p>
          </div>

          <div className="mt-8 space-y-6">
            <div 
              className="flex justify-center px-6 pt-5 pb-6 border-2 border-slate-700 border-dashed rounded-lg hover:border-cyan-500/50 transition-colors duration-200"
              onClick={() => fileInputRef.current.click()}
            >
              <div className="space-y-1 text-center">
                <FileText className="mx-auto h-12 w-12 text-slate-400" />
                <div className="flex text-sm text-slate-400">
                  <span className="relative cursor-pointer rounded-md font-medium text-cyan-400 hover:text-cyan-300">
                    Upload a file
                  </span>
                  <p className="pl-1">or drag and drop</p>
                </div>
                <p className="text-xs text-slate-400">
                  PDF or DOCX up to 10MB
                </p>
                <input
                  type="file"
                  ref={fileInputRef}
                  className="hidden"
                  accept=".pdf,.docx"
                  onChange={handleFileInputChange}
                />
              </div>
            </div>

            {file && (
              <div className="text-sm text-slate-400">
                Selected file: {file.name}
              </div>
            )}

            {error && (
              <div className="flex items-center gap-2 text-red-400">
                <AlertCircle className="h-5 w-5" />
                <span>{error}</span>
              </div>
            )}

            <button
              className="w-full flex items-center justify-center gap-2 bg-gradient-to-r from-cyan-500 to-blue-600 px-6 py-3 rounded-lg text-white font-medium shadow-lg shadow-cyan-500/20 hover:shadow-cyan-500/40 transition-all duration-200 hover:scale-105 disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:scale-100"
              onClick={handleFileUpload}
              disabled={loading || !file}
            >
              {loading ? (
                <>
                  <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white" />
                  Processing...
                </>
              ) : (
                <>
                  <Upload className="h-5 w-5" />
                  Upload Report
                </>
              )}
            </button>
          </div>
        </div>
      </main>
      <Footer />
    </div>
  );
};

export default UploadPage;